import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import JsonLd from "../../components/JsonLd";

const BlogLayout = ({ title, metaTitle, metaDesc, canonical, children, datePublished = "2026-01-15", dateModified = "2026-05-20" }) => {
  useEffect(() => { document.title = metaTitle; }, [metaTitle]);

  const articleSchemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": metaDesc,
    "author": { "@type": "Person", "name": "Steve Haynes", "url": "https://thecolivingadvisor.com" },
    "publisher": {
      "@type": "Organization",
      "name": "Savi Coliving",
      "logo": { "@type": "ImageObject", "url": "https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" }
    },
    "mainEntityOfPage": `https://www.savicoliving.com${canonical}`,
    "datePublished": datePublished,
    "dateModified": dateModified
  };
  const breadcrumbSchemaData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.savicoliving.com/" },
      { "@type": "ListItem", "position": 2, "name": "Sober Coliving", "item": "https://www.savicoliving.com/sober-coliving" },
      { "@type": "ListItem", "position": 3, "name": title, "item": `https://www.savicoliving.com${canonical}` }
    ]
  };

  return (
  <div className="min-h-screen bg-[#FBF7F1]">
    <Helmet>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={`https://www.savicoliving.com${canonical}`} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://www.savicoliving.com${canonical}`} />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
    <JsonLd id={`article-${canonical.replace(/\//g, '-')}`} data={articleSchemaData} />
    <JsonLd id={`breadcrumb-${canonical.replace(/\//g, '-')}`} data={breadcrumbSchemaData} />

    <header className="fixed top-0 left-0 right-0 z-50 bg-[#FBF7F1]/95 backdrop-blur-lg border-b border-stone-100">
      <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" alt="Savi" className="h-7 w-auto" />
          <span className="text-base tracking-wider text-[#C89B7B]"><span style={{fontFamily: 'Cinzel, serif'}}>SAVI</span> <span style={{fontFamily: "'Cormorant Garamond', serif"}}>Coliving</span></span>
        </Link>
        <Link to="/sober-coliving" className="text-sm text-[#C85A38] font-medium hover:underline">← Sober Coliving</Link>
      </div>
    </header>

    <article className="pt-24 pb-16 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-4 leading-tight">{title}</h1>
        <div className="w-12 h-0.5 bg-[#C85A38] mb-8" />
        <div className="prose-savi space-y-4 text-[#2E2A26]/60 text-sm leading-relaxed [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:text-[#2E2A26] [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-[#2E2A26]/80 [&_h3]:text-base [&_h3]:font-medium [&_h3]:mt-8 [&_h3]:mb-3 [&_strong]:text-[#2E2A26]/80">
          {children}
        </div>
      </div>
    </article>

    <section className="py-10 px-4 bg-[#F4ECE2]">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[#2E2A26] font-serif text-xl mb-3">Interested in sober coliving?</p>
        <p className="text-[#2E2A26]/50 text-sm mb-6">Savi is a sober coliving space in the mountains of Valencia. Application only.</p>
        <Link to="/">
          <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-4 text-sm font-medium transition-all">View Savi Coliving →</button>
        </Link>
      </div>
    </section>

    <footer className="bg-white py-6 px-4">
      <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-[#2E2A26]/40 text-xs">&copy; 2026 SAVI Coliving</p>
        <div className="flex gap-4 text-xs text-[#2E2A26]/40">
          <Link to="/sober-coliving" className="hover:text-[#2E2A26]">Sober Coliving</Link>
          <Link to="/privacy" className="hover:text-[#2E2A26]">Privacy</Link>
          <Link to="/" className="hover:text-[#2E2A26]">Home</Link>
        </div>
      </div>
    </footer>
  </div>
  );
};

export default BlogLayout;
